import React, { useState, useEffect, useLayoutEffect } from "react";
import { Outlet, useParams } from "react-router-dom";

import FlexPackDetailsContext from "../../context/FlexPackDetails/FlexPackDetails";
import { BannerProvider } from "../../providers/BannerProvider/BannerProvider";

import { getFlexPack } from "../../utilities/api";

import {
  getNowInTimezone,
  getTimezoneDate,
  timezones,
} from "../../utilities/helpers";

import {
  PageLoadingContainer,
  Sidenav,
  FlexPackBanner,
} from "../../components";

export default function MyFlexPackPage() {
  const { uuid } = useParams();

  const [windowSize, setWindowSize] = useState(undefined);

  const [flexPack, setFlexPack] = useState();

  const [flexPackContext, setFlexPackContext] = useState();

  const [flexPackVisibility, setFlexPackVisibility] = useState(null);

  const [flexPackStart, setFlexPackStart] = useState(null);

  const [flexPackEnd, setFlexPackEnd] = useState(null);

  const [canPublish, setCanPublish] = useState(false);

  const [isFlexPackPublished, setIsFlexPackPublished] = useState(false);

  const [isFlexPackOnsale, setIsFlexPackOnsale] = useState(false);

  const [isFlexPackSoldout, setIsFlexPackSoldout] = useState(false);

  const [hasFlexPackEnded, setHasFlexPackEnded] = useState(false);

  const [isLoading, setIsLoading] = useState(true); // default to true

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      const el = document.querySelector("html");
      // Set window width/height to state
      setWindowSize(el.clientWidth);
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  });

  useLayoutEffect(() => {
    const el = document.querySelector("#banner");
    if (el) el.style.width = `${windowSize}px`;
  }, [windowSize]);

  useEffect(() => {
    setIsLoading(true);
    getFlexPack(uuid)
      .then((res) => {
        setFlexPack(res?.data[0]);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
    // get flex pack here
  }, [uuid]);

  useEffect(() => {
    // set state for package details context
    updateFlexPack(flexPack);
    setFlexPackStart(flexPack?.start);
    setFlexPackEnd(flexPack?.end);
    const visibility = flexPack?.visibility;
    setFlexPackVisibility(visibility ? visibility : null);
    updateCanPublish(flexPack?.image?.id);
    updateIsFlexPackOnsale(flexPack);
    updateIsFlexPackSoldout(flexPack);
    updateHasFlexPackEnded(flexPack);
    updateIsFlexPackPublished(flexPack);
  }, [flexPack]);

  const updateFlexPack = (flexPack) => {
    setFlexPackContext(flexPack);
  };

  // can publish when flex pack is published or flex pack is onsale or flex pack details are filled out
  const updateCanPublish = (image = flexPackContext?.image?.id) => {
    setCanPublish(
      flexPackContext?.status === "published" ||
        flexPackContext?.status === "on_sale" ||
        Boolean(image),
    );
  };

  // switching automatically from published to onsale
  // flex pack is onsale when flex pack is onsale or start date is same or before now
  const updateIsFlexPackOnsale = (flexPack = flexPackContext) => {
    // update only if flex pack if onsale or published or ended
    if (
      (flexPack && flexPack?.status === "published") ||
      flexPack?.status === "on_sale" ||
      flexPack?.status === "complete"
    ) {
      const now = getNowInTimezone(flexPack.timezone);
      const startTime = getTimezoneDate(flexPack.start, flexPack.timezone);
      // false -> flex pack has ended
      // true -> flex pack is onsale
      // true -> start date is same or before now
      setIsFlexPackOnsale(
        flexPack?.status !== "complete"
          ? flexPack?.status === "on_sale" || startTime.isSameOrBefore(now)
          : false,
      );
      // if flex pack is onsale or not onsale, it is published to prevent from publishing again
      setIsFlexPackPublished(true);
    }
  };

  // switching automatically from scheduled or unpublished to published
  // flex pack is published when flex pack is published or visibility date is same or before now
  const updateIsFlexPackPublished = (flexPack = flexPackContext) => {
    // update only if canPublish and flex pack is not onsale - can be onsale at same time it is published, don't want to change status to published
    if (
      flexPack &&
      canPublish &&
      flexPack?.status !== "on_sale" &&
      flexPack?.visibility
    ) {
      // false -> flex pack has ended
      // true -> flex pack is published
      // true -> visibility date is same or before now
      setIsFlexPackPublished(
        flexPack?.status !== "complete"
          ? flexPack?.status === "published" ||
              getTimezoneDate(
                flexPack?.visibility,
                flexPack.timezone,
              ).isSameOrBefore(getNowInTimezone(flexPack.timezone))
          : false,
      );
    }
  };

  // switching automatically from onsale to soldout
  // flex pack is soldout when flex pack is soldout
  const updateIsFlexPackSoldout = (flexPack = flexPackContext) => {
    // update only if flex pack is onsale
    if (
      flexPack &&
      (flexPack?.status === "on_sale" || flexPack?.status === "complete")
    ) {
      // true -> flexPack is soldout
      setIsFlexPackSoldout(flexPack?.isSoldOut);
    }
  };

  // switching automatically from draft, published, hidden, onsale to complete
  // flex pack has ended when flex pack has ended or end is same or before now
  const updateHasFlexPackEnded = (flexPack = flexPackContext) => {
    // if flex pack has an end date - is existing flex pack
    if (flexPack && flexPack?.end) {
      const now = getNowInTimezone(flexPack.timezone);
      const endTime = getTimezoneDate(flexPack.end, flexPack.timezone);

      // true -> flex pack has ended
      // true -> end time is same or before now
      setHasFlexPackEnded(
        flexPack?.status === "complete" || endTime.isSameOrBefore(now),
      );
    }
  };

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <>
          <FlexPackDetailsContext.Provider
            value={{
              flexPack: flexPackContext,
              updateFlexPack,
              flexPackStart,
              storeFlexPackStart: setFlexPackStart,
              flexPackEnd,
              storeFlexPackEnd: setFlexPackEnd,
              flexPackVisibility,
              storeFlexPackVisibility: setFlexPackVisibility,
              canPublish,
              updateCanPublish,
              isFlexPackPublished,
              updateIsFlexPackPublished,
              isFlexPackOnsale,
              updateIsFlexPackOnsale,
              isFlexPackSoldout,
              updateIsFlexPackSoldout,
              hasFlexPackEnded,
              updateHasFlexPackEnded,
            }}
          >
            <BannerProvider>
              <FlexPackBanner />
            </BannerProvider>
            <Sidenav />
            <div className="spacer-md spacer-md--with-banner" id="main-content">
              <Outlet />
            </div>
          </FlexPackDetailsContext.Provider>
        </>
      )}
    </>
  );
}
